/**
 * Booking Form Store
 *
 * Pinia store for managing itinerary form state.
 * Replaces the $refs-based communication between FormTab and ServiceItem.
 *
 * This store:
 * - Manages booking items state centrally
 * - Calculates totals reactively using domain services
 * - Tracks form dirty state
 * - Provides API-ready form data
 */
import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import { itineraryPricingService } from '../services/itinerary-pricing.service';
// =============================================================================
// STORE
// =============================================================================
export const useBookingFormStore = defineStore('bookingForm', () => {
    // ---------------------------------------------------------------------------
    // STATE
    // ---------------------------------------------------------------------------
    // Customer information
    const customerId = ref(0);
    const customerName = ref('');
    const customerEmail = ref('');
    // Booking items array
    const bookingItems = ref([]);
    // Form metadata
    const tags = ref([]);
    const state = ref('PENDING');
    const isPayment = ref(false);
    // Editing state
    const editingItineraryId = ref(null);
    const isDirty = ref(false);
    // ---------------------------------------------------------------------------
    // GETTERS
    // ---------------------------------------------------------------------------
    /**
     * Calculate totals using pricing service
     */
    const totals = computed(() => {
        return itineraryPricingService.calculateItineraryTotals(bookingItems.value);
    });
    /**
     * Get tax rate display string
     */
    const taxRateDisplay = computed(() => {
        return itineraryPricingService.getTaxRateDisplay(bookingItems.value);
    });
    /**
     * Check if form has valid customer and at least one booking item
     */
    const isFormValid = computed(() => {
        return customerId.value > 0 && bookingItems.value.length > 0;
    });
    /**
     * Check if form has any content
     */
    const hasContent = computed(() => {
        return customerId.value > 0 || bookingItems.value.length > 0;
    });
    /**
     * Get date range from booking items
     */
    const dateRange = computed(() => {
        if (bookingItems.value.length === 0) {
            return { start: null, end: null };
        }
        const dates = bookingItems.value
            .filter((item) => item.start_datetime)
            .map((item) => new Date(item.start_datetime))
            .sort((a, b) => a.getTime() - b.getTime());
        const endDates = bookingItems.value
            .filter((item) => item.end_datetime)
            .map((item) => new Date(item.end_datetime))
            .sort((a, b) => b.getTime() - a.getTime());
        return {
            start: dates[0] || null,
            end: endDates[0] || null,
        };
    });
    /**
     * Get total number of services
     */
    const totalServices = computed(() => bookingItems.value.length);
    /**
     * Check if editing existing itinerary
     */
    const isEditMode = computed(() => editingItineraryId.value !== null);
    // ---------------------------------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------------------------------
    /**
     * Set customer information
     */
    function setCustomer(info) {
        customerId.value = info.id;
        customerName.value = info.name;
        customerEmail.value = info.email || '';
        markDirty();
    }
    /**
     * Clear customer information
     */
    function clearCustomer() {
        customerId.value = 0;
        customerName.value = '';
        customerEmail.value = '';
        markDirty();
    }
    /**
     * Add a new booking item
     */
    function addBookingItem(item) {
        bookingItems.value.push(item);
        markDirty();
    }
    /**
     * Update a booking item at specific index
     */
    function updateBookingItem(index, item) {
        if (index >= 0 && index < bookingItems.value.length) {
            bookingItems.value[index] = item;
            markDirty();
        }
    }
    /**
     * Remove a booking item at specific index
     */
    function removeBookingItem(index) {
        if (index >= 0 && index < bookingItems.value.length) {
            bookingItems.value.splice(index, 1);
            markDirty();
        }
    }
    /**
     * Move booking item position
     */
    function moveBookingItem(fromIndex, toIndex) {
        if (fromIndex >= 0 &&
            fromIndex < bookingItems.value.length &&
            toIndex >= 0 &&
            toIndex < bookingItems.value.length) {
            const [item] = bookingItems.value.splice(fromIndex, 1);
            bookingItems.value.splice(toIndex, 0, item);
            markDirty();
        }
    }
    /**
     * Get booking item by index
     */
    function getBookingItem(index) {
        return bookingItems.value[index];
    }
    /**
     * Set form tags
     */
    function setTags(newTags) {
        tags.value = [...newTags];
        markDirty();
    }
    /**
     * Set form state
     */
    function setState(newState) {
        state.value = newState;
        markDirty();
    }
    /**
     * Set payment status
     */
    function setPaymentStatus(isPaymentStatus) {
        isPayment.value = isPaymentStatus;
        markDirty();
    }
    /**
     * Mark form as dirty
     */
    function markDirty() {
        isDirty.value = true;
    }
    /**
     * Mark form as clean
     */
    function markClean() {
        isDirty.value = false;
    }
    /**
     * Load data for editing existing itinerary
     */
    function loadForEdit(itineraryId, customer, items, formTags = [], formState = 'PENDING') {
        editingItineraryId.value = itineraryId;
        customerId.value = customer.id;
        customerName.value = customer.name;
        customerEmail.value = customer.email || '';
        bookingItems.value = [...items];
        tags.value = [...formTags];
        state.value = formState;
        isDirty.value = false;
    }
    /**
     * Get form data ready for API submission
     * Note: quantity_of_participants is per-booking-item, not global
     */
    function getFormData() {
        return {
            customer_id: customerId.value,
            booking_items: bookingItems.value.map((item) => ({
                service_id: item.service_id,
                service_slot_id: item.service_slot_id || null,
                start_datetime: item.start_datetime,
                end_datetime: item.end_datetime,
                quantity_of_participants: item.quantity_of_participants,
                custom_field_values: item.custom_field_values || {},
            })),
            tags: tags.value,
            state: state.value,
        };
    }
    /**
     * Reset store to initial state
     */
    function reset() {
        customerId.value = 0;
        customerName.value = '';
        customerEmail.value = '';
        bookingItems.value = [];
        tags.value = [];
        state.value = 'PENDING';
        isPayment.value = false;
        editingItineraryId.value = null;
        isDirty.value = false;
    }
    /**
     * Alias for reset (Pinia convention)
     */
    function $reset() {
        reset();
    }
    // ---------------------------------------------------------------------------
    // WATCHERS
    // ---------------------------------------------------------------------------
    // Auto-mark dirty when booking items change deeply
    watch(bookingItems, () => {
        // Skip marking dirty during initial load
        if (editingItineraryId.value !== null && !isDirty.value) {
            return;
        }
    }, { deep: true });
    // ---------------------------------------------------------------------------
    // RETURN
    // ---------------------------------------------------------------------------
    return {
        // State
        customerId,
        customerName,
        customerEmail,
        bookingItems,
        tags,
        state,
        isPayment,
        editingItineraryId,
        isDirty,
        // Getters
        totals,
        taxRateDisplay,
        isFormValid,
        hasContent,
        dateRange,
        totalServices,
        isEditMode,
        // Actions
        setCustomer,
        clearCustomer,
        addBookingItem,
        updateBookingItem,
        removeBookingItem,
        moveBookingItem,
        getBookingItem,
        setTags,
        setState,
        setPaymentStatus,
        markDirty,
        markClean,
        loadForEdit,
        getFormData,
        reset,
        $reset,
    };
});
